import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Package,
  Upload,
  Loader2,
  Tag,
  DollarSign,
  FileText,
  Hash,
  Star,
  ArrowLeft,
  Image as ImageIcon,
  CheckCircle,
  AlertCircle,
  FolderOpen,
} from 'lucide-react';
import clsx from 'clsx';
import { supabase, getImageUrl } from '../../lib/supabase';
import { useAdminCategories } from '../../services/adminService';
import { useUploadMedia, validateFile, formatFileSize } from '../../services/mediaService';
import { MediaLibraryModal } from '../../components/MediaLibraryModal/MediaLibraryModal';

/**
 * Add product form with image upload + media library integration.
 */
export default function AdminAddProductPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: categories } = useAdminCategories();
  const uploadMutation = useUploadMedia();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    category_id: '',
    is_featured: false,
    feature_priority: '',
  });

  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [imageError, setImageError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Auto-generate slug from title
  const handleTitleChange = (val) => {
    update('title', val);
    update('slug', val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError('');
    const check = validateFile(file);
    if (!check.valid) {
      setImageError(check.error);
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImagePath('');
  };

  // Handle image from media library
  const handleMediaSelect = (media) => {
    setImageFile(null);
    setImagePreview(media.url);
    setImagePath(media.file_path);
    setImageError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let finalImagePath = imagePath;

      // Upload image if a new file was selected
      if (imageFile && !imagePath) {
        setUploading(true);
        setUploadProgress(0);
        const result = await uploadMutation.mutateAsync({
          file: imageFile,
          onProgress: (pct) => setUploadProgress(pct),
        });
        finalImagePath = result.path;
        setUploading(false);
      }

      const payload = {
        ...form,
        price: Number(form.price),
        feature_priority: form.feature_priority ? Number(form.feature_priority) : null,
        category_id: form.category_id || null,
        image_path: finalImagePath || null,
      };

      const { error: dbError } = await supabase.from('products').insert(payload);
      if (dbError) throw dbError;

      // Invalidate product caches so lists update instantly
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      navigate('/admin/products');
    } catch (err) {
      setError(err.message || 'Failed to add product');
      setUploading(false);
    } finally {
      setLoading(false);
    }
  };

  const displayImage = imagePreview || (imagePath ? getImageUrl(imagePath) : null);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Add Product</h1>
          <p className="text-sm text-brand-gray mt-0.5">Create a new menu item</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-error/20 rounded-xl text-sm text-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-brand-white border border-ui-border rounded-2xl p-6 space-y-5">
        {/* ── Product Image ── */}
        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">
            Product Image
          </label>
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            <div className="w-24 h-24 rounded-xl overflow-hidden bg-brand-light border border-ui-border flex-shrink-0">
              {displayImage ? (
                <img src={displayImage} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon size={28} className="text-ui-muted" />
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl cursor-pointer transition-colors">
                  <Upload size={14} />
                  Choose File
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setMediaModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                >
                  <FolderOpen size={14} />
                  From Library
                </button>
              </div>

              {imageFile && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-brand-gray truncate max-w-[160px]">
                    {imageFile.name}
                  </span>
                  <span className="text-[10px] text-ui-muted">
                    ({formatFileSize(imageFile.size)})
                  </span>
                </div>
              )}

              {imagePath && !imageFile && (
                <span className="flex items-center gap-1 text-xs text-success font-medium">
                  <CheckCircle size={12} />
                  Image selected from library
                </span>
              )}

              {/* Upload progress */}
              {uploading && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-brand-red" />
                    <span className="text-xs font-medium text-brand-gray">
                      Uploading… {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-brand-light rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      className="h-full bg-brand-red rounded-full"
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                </div>
              )}

              <p className="text-[10px] text-ui-muted">
                Max 5 MB · JPG, PNG, WebP, GIF, SVG
              </p>
              {imageError && (
                <div className="flex items-center gap-1 text-xs text-error">
                  <AlertCircle size={12} />
                  {imageError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Product Name</label>
          <div className="relative">
            <Package size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Classic Smash Burger"
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
            />
          </div>
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Slug</label>
          <div className="relative">
            <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              required
              value={form.slug}
              onChange={(e) => update('slug', e.target.value)}
              placeholder="classic-smash-burger"
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
            />
          </div>
        </div>

        {/* Category + Price row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-brand-dark mb-1.5">Category</label>
            <div className="relative">
              <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
              <select
                value={form.category_id}
                onChange={(e) => update('category_id', e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 appearance-none"
              >
                <option value="">Select category</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-brand-dark mb-1.5">Price (KES)</label>
            <div className="relative">
              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
              <input
                type="number"
                required
                min="0"
                value={form.price}
                onChange={(e) => update('price', e.target.value)}
                placeholder="850"
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
              />
            </div>
          </div>
        </div>

        {/* Featured row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer py-2.5">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => update('is_featured', e.target.checked)}
                className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
              />
              <Star size={16} className="text-brand-red" />
              <span className="text-sm font-medium text-brand-dark">Featured product</span>
            </label>
          </div>
        </div>

        {/* Short description */}
        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Short Description</label>
          <input
            type="text"
            value={form.short_description}
            onChange={(e) => update('short_description', e.target.value)}
            placeholder="A brief tagline..."
            className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
          />
        </div>

        {/* Full description */}
        <div>
          <label className="block text-sm font-semibold text-brand-dark mb-1.5">Full Description</label>
          <div className="relative">
            <FileText size={16} className="absolute left-3 top-3 text-ui-muted" />
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Detailed description of the product..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted resize-none"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={loading || uploading}
            className={clsx(
              'flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all',
              loading || uploading
                ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
            )}
          >
            {(loading || uploading) && <Loader2 size={16} className="animate-spin" />}
            {uploading ? 'Uploading…' : loading ? 'Saving...' : 'Save Product'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Media library modal */}
      <MediaLibraryModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        onSelect={handleMediaSelect}
        selectMode
      />
    </div>
  );
}
